import _ from "underscore";
import classNames from "classnames";
import PropTypes from "prop-types";
import React from "react";
import ReactDOM from "react-dom";
import Dropdown from "../elements/dropdown";
import Icon from "../elements/icon";
import WindowUtils from "../../mixins/window-overflow-mixin";
import AdvancedComboboxContent from "./advanced-combobox-content";

const { array, object, string, any, func, bool, oneOfType } = PropTypes;

// Expects the follow formats:
// For Groups: {label: "Group Label", items: [...]}
// For Items: {label: "Option Label", value: "Option value", icon: "icon-class", disabled: true}

export default class extends React.Component {
  static displayName = "AdvancedCombobox";

  static propTypes = {
    options: oneOfType([array, object]),
    name: string,
    value: any,
    onChange: func,
    label: string,

    position: string,
    multiple: bool,
    grouped: bool,
    allowChangeAll: bool,
    allowUnselectAll: bool,

    searchable: bool,
    search_placeholder: string,
    initial_search: string,
    onSearch: func, // args: (new_search_string) # called when user types

    onOpen: func, // no args
    onClose: func, // no args

    createFromSearch: func, //

    formatItem: func, // args: (item, isSelected, isHovered, search_query, props)
    formatSelection: func, // args: (selectionText, selection, placeholder, open)
    placeholder: string,
    empty_placeholder: any,
    loading: bool,
    disabled: bool,
    closeOnEnter: bool,
  };

  static defaultProps = {
    placeholder: "Select Option...",
    formatSelection(selectionText, selection, placeholder, open) {
      return (
        <span>
          <span className="et--advanced-combobox--selected-text">{selectionText}</span>
          {open ? <Icon icon="chevron-up" /> : <Icon icon="chevron-down" />}
        </span>
      );
    },
  };

  state = {
    height: undefined,
    open: false,
  };

  componentDidMount() {
    this.mounted = true;
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  reset = () => {
    this.setState({
      height: undefined,
      open: false,
      reverse_open: false,
    });
  };

  handleOpen = () => {
    this.setState({ open: true });
    if (typeof this.props.onOpen === "function") {
      this.props.onOpen();
    }

    return _.defer(() => {
      if (this.mounted) {
        if (this.state.open) {
          const ref = this.advanced_combobox_content;
          const node = ReactDOM.findDOMNode(ref);

          const height = WindowUtils.calcHeight(node, { min: 175 });
          const content_height =
            height - __guard__(WindowUtils.getDOMProperties(ReactDOM.findDOMNode(this)), x => x.height);

          this.setState({
            height: content_height,
            reverse_open: height === 175,
          });
        }
      }
    });
  };

  handleClose = () => {
    if (this.state.open) {
      if (typeof this.props.onSearch === "function") {
        this.props.onSearch();
      }
      if (typeof this.props.onClose === "function") {
        this.props.onClose();
      }
      this.reset();
    }
  };

  handleComplete = () => {
    this.handleClose();
  };

  getOptionLabel = value => {
    const option = _.findWhere(this.props.options, { value });
    return (option != null ? option.label : undefined) || (option != null ? option.value : undefined);
  };

  getSelectionText = () => {
    const selection = this.props.value;
    const display_text = (() => {
      if (_.isArray(selection)) {
        // multi-select
        const num = _.size(selection);
        if (num > 1) {
          return `${num} Selected`;
        } else if (num === 1) {
          return selection[0].label || this.getOptionLabel(selection[0].value);
        }
      } else if (selection && Object.prototype.hasOwnProperty.call(selection, "value")) {
        // single select, and exists
        return selection.label || this.getOptionLabel(selection.value);
      }
    })();

    return display_text || this.props.placeholder;
  };

  render() {
    let { value, placeholder, header, formatSelection } = this.props;
    const { open } = this.state;

    header = header || formatSelection(this.getSelectionText(), value, placeholder, open);

    return (
      <Dropdown
        className={classNames(this.props.className, "et--advanced-combobox", {
          "is-opened-up": this.props.position === "up" || this.state.reverse_open,
        })}
        label={this.props.label}
        header={header}
        disabled={this.props.disabled}
        isOpen={this.state.open}
        onOpen={this.handleOpen}
        onClose={this.handleClose}
      >
        <AdvancedComboboxContent
          ref={e => {
            this.advanced_combobox_content = e;
          }}
          options={this.props.options}
          value={this.props.value}
          onChange={this.props.onChange}
          multiple={this.props.multiple}
          grouped={this.props.grouped}
          allowChangeAll={this.props.allowChangeAll}
          allowUnselectAll={this.props.allowUnselectAll}
          searchable={this.props.searchable}
          search_placeholder={this.props.search_placeholder}
          initial_search={this.props.initial_search}
          onSearch={this.props.onSearch}
          focusOnMount
          onClose={this.props.onClose}
          createFromSearch={this.props.createFromSearch}
          formatItem={this.props.formatItem}
          formatSelection={this.props.formatSelection}
          placeholder={this.props.placeholder}
          empty_placeholder={this.props.empty_placeholder}
          loading={this.props.loading}
          isOpenedUp={this.state.reverse_open || this.props.position === "up"}
          height={this.state.height}
          onComplete={this.handleComplete}
        />
        {// Allow for additional content form children
        this.props.children ? <div className="et--advanced-combobox--extra">{this.props.children}</div> : undefined}
      </Dropdown>
    );
  }
}

function __guard__(value, transform) {
  return typeof value !== "undefined" && value !== null ? transform(value) : undefined;
}
